import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const PORTRAIT =
  "https://static.prod-images.emergentagent.com/jobs/ed8b42dc-fcd7-44f9-94a6-aade7bbc0833/images/3eadf3c8c24edb490a251b5664c1e7ddfd82f4b024438fd99d3c9bba247547e8.jpeg";

const lines = ["Hai analizzato", "tutto.", "Adesso è tempo", "di agire."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.5 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-background"
      data-testid="hero-section"
    >
      {/* Portrait */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-y-0 right-0 w-full md:w-[52%] h-full"
      >
        <div className="relative h-full w-full overflow-hidden">
          <img
            src={PORTRAIT}
            alt="Francesco Galli"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="h-full w-full object-cover object-center opacity-70 md:opacity-90 grayscale-[15%]"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/70 md:via-background/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="flex items-center gap-4 mb-8"
        >
          <span className="h-px w-10 bg-primary" />
          <span className="text-[11px] md:text-xs tracking-[0.35em] text-primary">
            METODO SINTESI · MENTAL COACHING & PNL
          </span>
        </motion.div>

        <h1 className="font-serif font-light text-foreground leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] max-w-4xl">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {i === 3 ? (
                  <span>
                    di <span className="italic text-primary">agire.</span>
                  </span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-10 max-w-lg text-base md:text-lg text-muted-foreground leading-relaxed"
        >
          Metodo Sintesi unisce PNL e pragmatica dell'azione per aiutare
          imprenditori, professionisti e atleti a rompere i blocchi decisionali
          e trasformare la strategia in risultati concreti. Senza fuffa.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        >
          <button
            onClick={() => scrollToId("candidatura")}
            className="group inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-8 py-4 text-sm tracking-[0.12em] hover:bg-primary/90 transition-colors duration-300"
            data-testid="hero-cta-primary"
          >
            PRENOTA LA TUA SESSIONE DI ANALISI
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
              →
            </span>
          </button>
          <button
            onClick={() => scrollToId("metodo-sintesi")}
            className="inline-flex items-center gap-3 text-sm tracking-[0.12em] text-foreground/80 hover:text-primary transition-colors duration-300"
            data-testid="hero-cta-secondary"
          >
            SCOPRI IL METODO
          </button>
        </motion.div>
      </motion.div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
        className="absolute bottom-8 left-6 md:left-10 z-10 flex items-center gap-3 text-muted-foreground"
      >
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} className="text-primary" />
        </motion.span>
        <span className="text-[10px] tracking-[0.3em]">SCORRI</span>
      </motion.div>
    </section>
  );
}
